/**
 * ChurnMemberPage - 退群成员管理页面
 * 展示退群成员信息，支持按业务单元、群聊、日期等条件筛选和分页。
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  App,
  Avatar,
  Button,
  Card,
  DatePicker,
  Empty,
  Input,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import type { ColumnsType, TablePaginationConfig } from "antd/es/table";
import type { FilterValue, SorterResult } from "antd/es/table/interface";
import Dayjs from "dayjs";
import {
  CloudDownloadOutlined,
  ReloadOutlined,
  UserOutlined,
} from "../components/icons";
import { trpc } from "../trpc";

const { Text, Title } = Typography;
const { RangePicker } = DatePicker;

type AnyRecord = Record<string, any>;

function downloadBase64Xlsx(base64: string, fileName: string) {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  const blob = new Blob([bytes], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

function formatTime(value: string | null | undefined) {
  if (!value) return "-";
  try {
    return new Date(value).toLocaleString("zh-CN");
  } catch {
    return "-";
  }
}

function formatCurrency(value: number | string | null | undefined) {
  if (value == null || value === "") return "-";
  const num = typeof value === "string" ? Number(value) : value;
  if (Number.isNaN(num)) return "-";
  return new Intl.NumberFormat("zh-CN", {
    style: "currency",
    currency: "CNY",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
}

function enrichmentStatusTag(status: string | null | undefined) {
  switch (status) {
    case "enriched":
      return <Tag color="success">已沉淀</Tag>;
    case "pending":
      return <Tag color="processing">待沉淀</Tag>;
    case "failed":
      return <Tag color="error">失败</Tag>;
    default:
      return <Tag color="default">-</Tag>;
  }
}

function notificationStatusTag(
  status: string | null | undefined,
  error: string | null | undefined
) {
  const tag = (() => {
    switch (status) {
      case "notified":
        return <Tag color="success">已通知</Tag>;
      case "pending":
        return <Tag color="processing">未通知</Tag>;
      case "failed":
        return <Tag color="error">失败</Tag>;
      case "skipped":
        return <Tag color="default">无需通知</Tag>;
      default:
        return <Tag color="default">-</Tag>;
    }
  })();

  if (error) {
    return (
      <Tooltip title={error}>
        <Space size={4}>
          {tag}
          <Text type="danger" style={{ fontSize: 12 }}>
            !
          </Text>
        </Space>
      </Tooltip>
    );
  }
  return tag;
}

function buildFilterPayload(filters: {
  businessUnitId: string;
  roomId: string;
  keyword: string;
  startTime: string;
  endTime: string;
  enrichmentStatus: string;
  notificationStatus: string;
}) {
  const input: Record<string, unknown> = {};
  if (filters.businessUnitId) input.businessUnitId = filters.businessUnitId;
  if (filters.roomId) input.roomId = filters.roomId;
  if (filters.keyword) input.keyword = filters.keyword;
  if (filters.startTime) input.startTime = filters.startTime;
  if (filters.endTime) input.endTime = filters.endTime;
  if (filters.enrichmentStatus) input.enrichmentStatus = filters.enrichmentStatus;
  if (filters.notificationStatus) input.notificationStatus = filters.notificationStatus;
  return input;
}

export function ChurnMemberPage() {
  const { message } = App.useApp();

  // Filters
  const [filters, setFilters] = useState<{
    page: number;
    limit: number;
    businessUnitId: string;
    roomId: string;
    keyword: string;
    startTime: string;
    endTime: string;
    enrichmentStatus: string;
    notificationStatus: string;
    sortBy: "detectedAt";
    sortOrder: "asc" | "desc";
  }>({
    page: 1,
    limit: 20,
    businessUnitId: "",
    roomId: "",
    keyword: "",
    startTime: "",
    endTime: "",
    enrichmentStatus: "",
    notificationStatus: "",
    sortBy: "detectedAt",
    sortOrder: "desc",
  });

  // Data
  const [items, setItems] = useState<AnyRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [businessUnits, setBusinessUnits] = useState<AnyRecord[]>([]);
  const [businessUnitsLoading, setBusinessUnitsLoading] = useState(false);

  const loadBusinessUnits = useCallback(async () => {
    setBusinessUnitsLoading(true);
    try {
      const units = await trpc.fadan.businessUnits.list.query();
      setBusinessUnits(units);
    } catch {
      // Non-critical; filter just won't show options
    } finally {
      setBusinessUnitsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBusinessUnits();
  }, [loadBusinessUnits]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const input: Record<string, unknown> = {
        ...buildFilterPayload(filters),
        page: filters.page,
        limit: filters.limit,
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder,
      };

      const result = await trpc.fadan.churnMembers.list.query(input as any);
      setItems(result.items ?? []);
      setTotal(result.total ?? 0);
    } catch (err: any) {
      const msg = err?.message || "加载退群成员数据失败";
      setError(msg);
      message.error(msg);
    } finally {
      setLoading(false);
    }
  }, [filters, message]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSearch = useCallback(() => {
    setFilters((prev) => ({ ...prev, page: 1 }));
  }, []);

  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      const result = await trpc.fadan.churnMembers.export.mutate(
        buildFilterPayload(filters) as any
      );
      downloadBase64Xlsx(result.base64, result.fileName);
      message.success(`已导出 ${result.rowCount} 条`);
    } catch (err: any) {
      message.error(err?.message || "导出失败");
    } finally {
      setExporting(false);
    }
  }, [filters, message]);

  const handleTableChange = useCallback(
    (
      pagination: TablePaginationConfig,
      _tableFilters: Record<string, FilterValue | null>,
      sorter: SorterResult<AnyRecord> | SorterResult<AnyRecord>[]
    ) => {
      const next = Array.isArray(sorter) ? sorter[0] : sorter;
      const sortOrder =
        next?.field === "detectedAt" && next.order === "ascend"
          ? ("asc" as const)
          : ("desc" as const);
      setFilters((prev) => ({
        ...prev,
        page: pagination.current ?? prev.page,
        limit: pagination.pageSize ?? prev.limit,
        sortBy: "detectedAt",
        sortOrder,
      }));
    },
    []
  );

  const businessUnitOptions = useMemo(
    () =>
      businessUnits.map((unit) => ({
        label: String(unit.name || unit.id || ""),
        value: String(unit.id || ""),
      })),
    [businessUnits]
  );

  const roomOptions = useMemo(() => {
    const units = filters.businessUnitId
      ? businessUnits.filter((unit) => String(unit.id) === filters.businessUnitId)
      : businessUnits;

    const rooms = units.flatMap((unit) => {
      const unitName = String(unit.name || unit.id || "");
      return (unit.groups || []).flatMap((group: AnyRecord) =>
        (group.rooms || []).map((room: AnyRecord) => ({
          value: String(room.roomId || ""),
          roomName: String(room.roomName || room.roomId || ""),
          unitName,
          enabled: room.enabled !== false,
        }))
      );
    });

    const seen = new Set<string>();
    return rooms
      .filter((room: { value: string }) => {
        if (!room.value || seen.has(room.value)) return false;
        seen.add(room.value);
        return true;
      })
      .map(
        (room: {
          value: string;
          roomName: string;
          unitName: string;
          enabled: boolean;
        }) => {
          const base = `${room.roomName}${room.enabled ? "" : "（已停用）"}`;
          const label = filters.businessUnitId
            ? base
            : room.unitName
              ? `${base} · ${room.unitName}`
              : base;
          return { label, value: room.value };
        }
      )
      .sort((a: { label: string }, b: { label: string }) =>
        a.label.localeCompare(b.label, "zh-CN")
      );
  }, [businessUnits, filters.businessUnitId]);

  const handleBusinessUnitChange = useCallback(
    (value: string | undefined) => {
      const nextUnitId = value ?? "";
      setFilters((prev) => {
        let nextRoomId = prev.roomId;
        if (nextRoomId) {
          const units = nextUnitId
            ? businessUnits.filter((unit) => String(unit.id) === nextUnitId)
            : businessUnits;
          const validIds = new Set(
            units.flatMap((unit) =>
              (unit.groups || []).flatMap((group: AnyRecord) =>
                (group.rooms || []).map((room: AnyRecord) => String(room.roomId || ""))
              )
            )
          );
          if (!validIds.has(nextRoomId)) {
            nextRoomId = "";
          }
        }
        return {
          ...prev,
          businessUnitId: nextUnitId,
          roomId: nextRoomId,
          page: 1,
        };
      });
    },
    [businessUnits]
  );

  const columns: ColumnsType<AnyRecord> = [
    {
      title: "检测时间",
      dataIndex: "detectedAt",
      key: "detectedAt",
      width: 170,
      render: (value: string | null | undefined) => (
        <Text style={{ fontVariantNumeric: "tabular-nums" }}>
          {formatTime(value)}
        </Text>
      ),
      defaultSortOrder: "descend",
      sortOrder: filters.sortOrder === "asc" ? "ascend" : "descend",
      sorter: true,
    },
    {
      title: "业务单元",
      dataIndex: "businessUnitName",
      key: "businessUnitName",
      width: 140,
      render: (value: string | null | undefined) => value || "-",
    },
    {
      title: "群信息",
      key: "room",
      width: 220,
      render: (_: unknown, record: AnyRecord) => {
        const name = record.roomName as string | undefined;
        const wxid = record.roomWxid as string | undefined;
        const roomId = record.roomId as string | undefined;
        return (
          <Space direction="vertical" size={2}>
            {name ? <Text>{name}</Text> : null}
            {wxid ? (
              <Text copyable={{ text: wxid }} type="secondary" style={{ fontSize: 12 }}>
                {wxid}
              </Text>
            ) : null}
            {roomId ? (
              <Text copyable={{ text: roomId }} type="secondary" style={{ fontSize: 11 }}>
                roomId {roomId}
              </Text>
            ) : null}
            {!name && !wxid && !roomId ? <Text type="secondary">-</Text> : null}
          </Space>
        );
      },
    },
    {
      title: "用户",
      key: "user",
      width: 240,
      render: (_: unknown, record: AnyRecord) => {
        const avatar = record.avatar as string | undefined;
        const nickname = record.nickname as string | undefined;
        const wxid = record.wxid as string | undefined;
        const qwId = record.qwId as string | undefined;
        const taokeId = record.taokeId as string | undefined;
        const externalUserId = record.externalUserId as string | undefined;
        return (
          <Space size={8}>
            <Avatar
              size={32}
              src={avatar || undefined}
              icon={!avatar ? <UserOutlined /> : undefined}
            />
            <Space direction="vertical" size={0}>
              {nickname ? <Text>{nickname}</Text> : null}
              {wxid ? (
                <Text copyable={{ text: wxid }} type="secondary" style={{ fontSize: 12 }}>
                  {wxid}
                </Text>
              ) : null}
              {externalUserId ? (
                <Text copyable={{ text: externalUserId }} type="secondary" style={{ fontSize: 11 }}>
                  ext {externalUserId}
                </Text>
              ) : null}
              {taokeId ? (
                <Text copyable={{ text: taokeId }} type="secondary" style={{ fontSize: 11 }}>
                  UID {taokeId}
                </Text>
              ) : null}
              {qwId ? (
                <Text copyable={{ text: qwId }} type="secondary" style={{ fontSize: 11 }}>
                  企微 {qwId}
                </Text>
              ) : null}
              {!nickname && !wxid && !externalUserId ? <Text type="secondary">-</Text> : null}
            </Space>
          </Space>
        );
      },
    },
    {
      title: "已支付订单",
      dataIndex: "payOrder",
      key: "payOrder",
      width: 110,
      align: "right",
      render: (value: number | null | undefined) =>
        value != null ? value : "-",
    },
    {
      title: "付款金额",
      dataIndex: "totalPayPrice",
      key: "totalPayPrice",
      width: 130,
      align: "right",
      render: (value: number | string | null | undefined) =>
        formatCurrency(value),
    },
    {
      title: "佣金",
      dataIndex: "totalCommission",
      key: "totalCommission",
      width: 130,
      align: "right",
      render: (value: number | string | null | undefined) =>
        formatCurrency(value),
    },
    {
      title: "UID",
      dataIndex: "taokeId",
      key: "uid",
      width: 140,
      render: (value: string | null | undefined) =>
        value ? <Text copyable={{ text: value }}>{value}</Text> : "-",
    },
    {
      title: "等级",
      dataIndex: "level",
      key: "level",
      width: 80,
      render: (value: string | null | undefined) => value || "-",
    },
    {
      title: "沉淀来源",
      dataIndex: "enrichmentSource",
      key: "enrichmentSource",
      width: 120,
      render: (value: string | null | undefined) => value || "-",
    },
    {
      title: "沉淀状态",
      dataIndex: "enrichmentStatus",
      key: "enrichmentStatus",
      width: 100,
      render: (value: string | null | undefined) =>
        enrichmentStatusTag(value),
    },
    {
      title: "通知状态",
      key: "notification",
      width: 130,
      render: (_: unknown, record: AnyRecord) =>
        notificationStatusTag(
          record.notificationStatus as string | null | undefined,
          record.notificationError as string | null | undefined
        ),
    },
  ];

  const filterBar = (
    <Card variant="borderless" className="shadow-sm">
      <div
        style={{
          display: "flex",
          gap: 12,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <Select
          allowClear
          placeholder="业务单元"
          style={{ minWidth: 180 }}
          value={filters.businessUnitId || undefined}
          loading={businessUnitsLoading}
          options={businessUnitOptions}
          onChange={handleBusinessUnitChange}
        />
        <Select
          allowClear
          showSearch
          optionFilterProp="label"
          placeholder="选择群聊"
          style={{ minWidth: 240 }}
          value={filters.roomId || undefined}
          loading={businessUnitsLoading}
          options={roomOptions}
          onChange={(value) =>
            setFilters((prev) => ({
              ...prev,
              roomId: value ?? "",
              page: 1,
            }))
          }
        />
        <Input.Search
          allowClear
          placeholder="昵称 / wxid"
          style={{ width: 200 }}
          value={filters.keyword}
          onChange={(e) =>
            setFilters((prev) => ({
              ...prev,
              keyword: e.target.value,
              page: 1,
            }))
          }
          onSearch={handleSearch}
        />
        <RangePicker
          placeholder={["开始日期", "结束日期"]}
          value={
            filters.startTime && filters.endTime
              ? [Dayjs(filters.startTime), Dayjs(filters.endTime)]
              : null
          }
          onChange={(dates) => {
            const start = dates?.[0]?.startOf("day").toISOString() ?? "";
            const end = dates?.[1]?.endOf("day").toISOString() ?? "";
            setFilters((prev) => ({
              ...prev,
              startTime: start,
              endTime: end,
              page: 1,
            }));
          }}
        />
        <Select
          allowClear
          placeholder="沉淀状态"
          style={{ minWidth: 120 }}
          value={filters.enrichmentStatus || undefined}
          options={[
            { label: "已沉淀", value: "enriched" },
            { label: "待沉淀", value: "pending" },
            { label: "失败", value: "failed" },
          ]}
          onChange={(value) =>
            setFilters((prev) => ({
              ...prev,
              enrichmentStatus: value ?? "",
              page: 1,
            }))
          }
        />
        <Select
          allowClear
          placeholder="通知状态"
          style={{ minWidth: 120 }}
          value={filters.notificationStatus || undefined}
          options={[
            { label: "已通知", value: "notified" },
            { label: "未通知", value: "pending" },
            { label: "失败", value: "failed" },
            { label: "无需通知", value: "skipped" },
          ]}
          onChange={(value) =>
            setFilters((prev) => ({
              ...prev,
              notificationStatus: value ?? "",
              page: 1,
            }))
          }
        />
      </div>
    </Card>
  );

  return (
    <div
      className="fade-in"
      style={{ display: "flex", flexDirection: "column", gap: 20 }}
    >
      {/* Page header */}
      <div className="page-title-row">
        <div>
          <Title level={3} style={{ marginBottom: 4 }}>
            退群成员
          </Title>
          <Text type="secondary">
            查看已检测到的退群成员信息，按业务单元和日期范围筛选。
          </Text>
        </div>
        <Space>
          <Button
            icon={<CloudDownloadOutlined />}
            onClick={handleExport}
            loading={exporting}
          >
            导出
          </Button>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => {
              setFilters((prev) => ({ ...prev, page: 1 }));
            }}
            loading={loading}
          >
            刷新
          </Button>
        </Space>
      </div>

      {/* Filter bar */}
      {filterBar}

      {/* Table */}
      <Card variant="borderless" className="shadow-sm">
        <Spin spinning={loading}>
          {error && !loading ? (
            <Card variant="borderless">
              <Empty
                description={
                  <Space direction="vertical" size={8}>
                    <Text type="danger">{error}</Text>
                    <Button
                      size="small"
                      icon={<ReloadOutlined />}
                      onClick={loadData}
                    >
                      重试
                    </Button>
                  </Space>
                }
              />
            </Card>
          ) : (
            <Table
              rowKey={(record) =>
                (record.id ?? record._id ?? Math.random().toString()) as string
              }
              columns={columns}
              dataSource={items}
              scroll={{ x: 2000 }}
              onChange={handleTableChange}
              locale={{
                emptyText: loading ? (
                  <Text type="secondary">加载中...</Text>
                ) : (
                  <Empty
                    description={
                      <Text type="secondary">暂无退群成员记录</Text>
                    }
                  />
                ),
              }}
              pagination={{
                current: filters.page,
                pageSize: filters.limit,
                total,
                showSizeChanger: true,
                showTotal: (count) => `共 ${count} 条`,
              }}
            />
          )}
        </Spin>
      </Card>
    </div>
  );
}
